import React, { useEffect, useMemo, useState } from 'react';
import {
  Spin,
  Empty,
  Tabs,
  Card,
  Row,
  Col,
  Tag,
  Progress,
  Collapse,
  Table,
  Tooltip,
  Typography,
  Divider,
  Select,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { db } from '../firebase/Firebase';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Text, Title } = Typography;

const DEFAULT_ACADEMIC_YEAR = '2025-2026';

// Color coding function for percentage-based metrics
// Using more professional, less punitive colors
const getColorForPercentage = (value) => {
  if (value >= 80) return '#52c41a'; // Professional green
  if (value >= 60) return '#faad14'; // Warm yellow-orange
  if (value >= 40) return '#fa8c16'; // Orange
  return '#597ef7'; // Blue instead of red - "getting started" not "failing"
};

// Background colors for metric cards
const getCardStyleForMetric = (type) => {
  const styles = {
    assignments: {
      backgroundColor: '#f0f5ff',
      borderColor: '#597ef7',
    },
    assessments: {
      backgroundColor: '#f6ffed',
      borderColor: '#52c41a',
    },
    dataTeams: {
      backgroundColor: '#f9f0ff',
      borderColor: '#722ed1',
    },
    mtss: {
      backgroundColor: '#fff7e6',
      borderColor: '#fa8c16',
    },
  };
  return styles[type] || {};
};

const METRIC_LABELS = {
  assessmentDeliveryPct: { label: 'Assessment Delivery', format: 'percent' },
  schoolsReporting: { label: 'Schools Reporting', format: 'number' },
  teacherParticipationPct: { label: 'Teacher Participation', format: 'percent' },
  studentVoiceResponsePct: { label: 'Student Voice Response', format: 'percent' },
  plcCount: { label: 'PLC Cycles Logged', format: 'number' },
  plcCyclesLogged: { label: 'PLC Cycles Logged', format: 'number' },
  dataTeamCycles: { label: 'Data Team Cycles', format: 'number' },
  dataTeamCount: { label: 'Data Team Cycles', format: 'number' },
  mtssCaseCount: { label: 'MTSS Cases', format: 'number' },
};

const formatMetricValue = (key, value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  const meta = METRIC_LABELS[key] || {};
  if (meta.format === 'percent') {
    return `${Number(value).toFixed(1)}%`;
  }
  return Number(value).toLocaleString();
};

const aggregateDistrictSummary = (usage, learning) => {
  if (!usage || !Array.isArray(usage.schools)) {
    return {
      totalStudents: 0,
      totalTeachers: 0,
      participatingTeachers: 0,
      totalPlc: 0,
      totalDataTeams: 0,
      mtssCases: 0,
      totalInteractions: 0,
    };
  }

  const summary = usage.schools.reduce(
    (acc, school) => {
      const summary = school.summary || {};
      const teachers = summary.teachers || {};
      const dataTeams = summary.dataTeams || {};
      const mtss = summary.mtss || {};

      acc.totalStudents += summary.studentCount || 0;
      acc.totalTeachers += teachers.total || 0;
      acc.participatingTeachers += teachers.participated || 0;
      acc.totalPlc += (summary.plc && summary.plc.total) || 0;
      acc.totalDataTeams += dataTeams.total || 0;
      acc.mtssCases += Object.values(mtss).reduce(
        (tierTotal, tier) => tierTotal + ((tier && tier.total) || 0),
        0,
      );

      return acc;
    },
    {
      totalStudents: 0,
      totalTeachers: 0,
      participatingTeachers: 0,
      totalPlc: 0,
      totalDataTeams: 0,
      mtssCases: 0,
      totalInteractions: 0,
    },
  );

  // Calculate total assessment interactions from Student Learning data
  // This is accurate because it counts actual assessment completions
  if (learning && Array.isArray(learning.standards)) {
    learning.standards.forEach(standard => {
      // Each standard.studentsAssessed represents actual assessment submissions
      summary.totalInteractions += standard.studentsAssessed || 0;
    });
  }

  return summary;
};

const sortGrades = (gradeA, gradeB) => {
  const gradeOrder = [
    'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
    '6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade',
    'Algebra', 'Geometry', 'Biology', 'Chemistry', 'Physics', 'United States History', 'World History'
  ];

  const aIndex = gradeOrder.indexOf(gradeA);
  const bIndex = gradeOrder.indexOf(gradeB);

  if (aIndex === -1 && bIndex === -1) return gradeA.localeCompare(gradeB);
  if (aIndex === -1) return 1;
  if (bIndex === -1) return -1;
  return aIndex - bIndex;
};

const getSchoolType = (school) => {
  const grades = school.grades || [];
  const gradeNames = grades.map(g => g.grade || '');

  // Check for elementary grades (K-5)
  const hasElementary = gradeNames.some(g =>
    ['Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade'].includes(g)
  );

  // Check for middle school grades (6-8)
  const hasMiddle = gradeNames.some(g =>
    ['6th Grade', '7th Grade', '8th Grade'].includes(g)
  );

  // Check for high school grades (9-12)
  const hasHigh = gradeNames.some(g =>
    ['9th Grade', '10th Grade', '11th Grade', '12th Grade'].includes(g)
  );

  // Determine school type based on grade ranges
  if (hasElementary && !hasMiddle && !hasHigh) return 'elementary';
  if (hasMiddle && !hasElementary && !hasHigh) return 'middle';
  if (hasHigh) return 'high';
  if (hasElementary && hasMiddle) return 'elementary'; // K-8 schools count as elementary

  return 'other';
};

const sortSchools = (schoolA, schoolB) => {
  const typeOrder = { elementary: 0, middle: 1, high: 2, other: 3 };

  const typeA = getSchoolType(schoolA);
  const typeB = getSchoolType(schoolB);

  const typeComparison = typeOrder[typeA] - typeOrder[typeB];

  // If same type, sort alphabetically by name
  if (typeComparison === 0) {
    const nameA = schoolA.schoolName || schoolA.schoolId || '';
    const nameB = schoolB.schoolName || schoolB.schoolId || '';
    return nameA.localeCompare(nameB);
  }

  return typeComparison;
};

const buildGradeRows = (grades = [], selectedQuarter = 'overall') => {
  // Sort grades first
  const sortedGrades = [...grades].sort((a, b) => sortGrades(a.grade, b.grade));

  return sortedGrades.map((grade) => {
    const teacherParticipation = grade.teacherParticipation || {};

    // Get quiz participation for the selected quarter
    const quizParticipation = selectedQuarter === 'overall'
      ? (grade.quizParticipation || {})
      : (grade.quizParticipationByQuarter || {})[selectedQuarter] || {};

    // Get assessment participation for the selected quarter
    const assessmentParticipation = selectedQuarter === 'overall'
      ? (grade.assessmentParticipation || {})
      : (grade.assessmentParticipationByQuarter || {})[selectedQuarter] || {};

    const studentVoice = grade.studentVoice || {};
    const dataTeams = grade.dataTeams || {};
    const mtss = grade.mtss || {};
    const mtssTotal = Object.values(mtss).reduce(
      (sum, tier) => sum + ((tier && tier.total) || 0),
      0,
    );

    return {
      key: grade.grade,
      grade: grade.grade,
      studentCount: grade.studentCount || 0,
      resourceAssignmentPct: quizParticipation.quizParticipationPct || 0,
      resourceAssignmentColor: getColorForPercentage(quizParticipation.quizParticipationPct || 0),
      teacherParticipationPct: teacherParticipation.participationPct || 0,
      studentVoicePct: studentVoice.responseRatePct || 0,
      assessmentsCompletedPct: assessmentParticipation.assessmentParticipationPct || 0,
      assessmentColor: getColorForPercentage(assessmentParticipation.assessmentParticipationPct || 0),
      dataTeams: dataTeams.total || 0,
      mtssCount: mtssTotal,
    };
  });
};

const gradeColumns = [
  {
    title: 'Grade',
    dataIndex: 'grade',
    key: 'grade',
    width: 120,
    render: (value) => <Text strong style={{ fontSize: 15, color: '#000000' }}>{value}</Text>,
  },
  {
    title: 'Students',
    dataIndex: 'studentCount',
    key: 'studentCount',
    width: 110,
    align: 'center',
    render: (value) => <Text style={{ color: '#000000', fontSize: 14 }}>{value?.toLocaleString()}</Text>,
  },
  {
    title: 'Quiz Participation',
    dataIndex: 'resourceAssignmentPct',
    key: 'resourceAssignmentPct',
    width: 200,
    render: (value, record) => (
      <div>
        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text strong style={{ color: '#000000', fontSize: 15 }}>
            {Number(value).toFixed(1)}%
          </Text>
        </div>
        <Progress
          percent={Number(value)}
          size="small"
          showInfo={false}
          strokeColor={record.resourceAssignmentColor}
        />
      </div>
    ),
  },
  {
    title: 'Assessment Participation',
    dataIndex: 'assessmentsCompletedPct',
    key: 'assessmentsCompletedPct',
    width: 200,
    render: (value, record) => (
      <div>
        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text strong style={{ color: '#000000', fontSize: 15 }}>
            {Number(value).toFixed(1)}%
          </Text>
          {Number(value) >= 85 && (
            <Tag color="success" style={{ marginLeft: 8, fontSize: 11 }}>Strong</Tag>
          )}
        </div>
        <Progress
          percent={Number(value)}
          size="small"
          showInfo={false}
          strokeColor={record.assessmentColor}
        />
      </div>
    ),
  },
];


const DistrictStatsV4 = (props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [learning, setLearning] = useState(null);
  const [activeTab, setActiveTab] = useState('system');
  const [selectedSchoolId, setSelectedSchoolId] = useState('ALL');
  const [selectedQuarter, setSelectedQuarter] = useState('overall');
  const [selectedSchoolType, setSelectedSchoolType] = useState('elementary');
  const [selectedContentArea, setSelectedContentArea] = useState('All Subjects');
  const [pacingGuidesCount, setPacingGuidesCount] = useState(0);

  const districtId = useMemo(() => {
    if (props.adminBaseProps && props.adminBaseProps.district) {
      return props.adminBaseProps.district.id;
    }
    if (
      props.instructionalFacilitatorBaseProps &&
      props.instructionalFacilitatorBaseProps.district
    ) {
      return props.instructionalFacilitatorBaseProps.district.id;
    }
    return '';
  }, [props.adminBaseProps, props.instructionalFacilitatorBaseProps]);

  let academicYear =
    (props.adminBaseProps && props.adminBaseProps.selectedAcademicYear) ||
    DEFAULT_ACADEMIC_YEAR;

  // HACK: Override for demo district to use 2024-2025 data
  if (districtId && districtId.startsWith('demo_b8afbef6f77b')) {
    academicYear = '2024-2025';
  }

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!districtId) {
        setLoading(false);
        setError('No district selected');
        return;
      }

      setLoading(true);
      setError(null);

      const docId = `${districtId}_${academicYear}`;
      console.log('[DistrictStatsV4] Loading data for docId:', docId);
      try {
        let activeYear = '2025'
        db.collection('pacingGuides')
          .where('districtId', '==', districtId)
          .where('year', '==', activeYear)
          .get()
          .then((querySnapshot) => {
            let pacingGuidesDict = {};
            querySnapshot.forEach((doc) => {
              let pacingGuide = doc.data();
              let pacingGuideKey = pacingGuide.standardCategory + '-' + pacingGuide.standardName;
              if (!pacingGuidesDict[pacingGuideKey]) {
                pacingGuidesDict[pacingGuideKey] = pacingGuide;
              }
            });
            let pacingGuidesCount = Object.keys(pacingGuidesDict).length;
            setPacingGuidesCount(pacingGuidesCount);
          })
          .catch((error) => {
            console.error("Error getting documents: ", error);
          });

        const [usageDoc, learningDoc] = await Promise.all([
          db.collection('districtAssessmentUsageV4').doc(docId).get(),
          db.collection('districtStudentLearningV4').doc(docId).get(),
        ]);

        if (!isMounted) return;

        if (!usageDoc.exists) {
          setError('District usage data not available yet.');
          setUsage(null);
        } else {
          // Fetch root usage data (contains systemCoherence)
          const rootUsageData = usageDoc.data().usage || usageDoc.data();
          console.log('[DistrictStatsV4] Root usage data:', rootUsageData);

          // Fetch all schools from subcollection
          const schoolsSnapshot = await db
            .collection('districtAssessmentUsageV4')
            .doc(docId)
            .collection('schools')
            .get();

          const schools = [];
          schoolsSnapshot.forEach((schoolDoc) => {
            const schoolData = schoolDoc.data();
            console.log('[DistrictStatsV4] Raw school doc data keys:', Object.keys(schoolData));
            if (schoolData.grades && schoolData.grades[0] && schoolData.grades[0].assessmentWindows) {
              console.log('[DistrictStatsV4] First window raw:', JSON.stringify(schoolData.grades[0].assessmentWindows[0]));
            }
            schools.push(schoolData);
          });
          console.log('[DistrictStatsV4] Fetched schools from subcollection:', schools.length);

          // Reconstruct full usage object with schools array
          const reconstructedUsage = {
            ...rootUsageData,
            schools: schools,
          };
          console.log('[DistrictStatsV4] Reconstructed usage object:', reconstructedUsage);
          console.log('[DistrictStatsV4] systemCoherence:', reconstructedUsage.systemCoherence);
          console.log('[DistrictStatsV4] bundles:', reconstructedUsage.systemCoherence?.bundles);
          setUsage(reconstructedUsage);
        }

        if (!learningDoc.exists) {
          setLearning(null);
        } else {
          // Fetch root learning data (contains mastery trends)
          const rootLearningData = learningDoc.data().learning || learningDoc.data();

          // Fetch all standards from subcollection
          const standardsSnapshot = await db
            .collection('districtStudentLearningV4')
            .doc(docId)
            .collection('standards')
            .get();

          const allStandards = [];
          standardsSnapshot.forEach((standardDoc) => {
            const data = standardDoc.data();
            if (data.standards && Array.isArray(data.standards)) {
              allStandards.push(...data.standards);
            }
          });

          // Reconstruct full learning object with standards array and total counts
          const reconstructedLearning = {
            ...rootLearningData,
            standards: allStandards,
            totalStandardsBySubjectGrade: rootLearningData.totalStandardsBySubjectGrade || {},
          };
          setLearning(reconstructedLearning);
        }
      } catch (err) {
        console.error('Failed to load district stats v4:', err);
        if (isMounted) {
          setError('Failed to load district analytics.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [districtId, academicYear]);

  const districtSummary = useMemo(() => aggregateDistrictSummary(usage, learning), [usage, learning]);

  const uniqueSubjects = useMemo(() => {
    if (!usage || !Array.isArray(usage.schools)) return [];
    const subjects = new Set();
    usage.schools.forEach((school) => {
      (school.grades || []).forEach((grade) => {
        (grade.assessmentWindows || []).forEach((window) => {
          if (window.subject) {
            subjects.add(window.subject);
          }
        });
      });
    });
    return Array.from(subjects);
  }, [usage]);

  const schools = useMemo(() => {
    if (!usage || !Array.isArray(usage.schools)) return [];
    return usage.schools.map(school => ({
      id: school.schoolId,
      name: school.schoolName || school.schoolId,
    }));
  }, [usage]);

  const filteredStandards = useMemo(() => {
    if (!learning || !Array.isArray(learning.standards)) return [];

    if (selectedSchoolId === 'ALL') {
      // Aggregate across all schools
      const aggregated = {};
      learning.standards.forEach(standard => {
        const key = `${standard.subject}|${standard.grade}|${standard.standardCode}`;
        if (!aggregated[key]) {
          aggregated[key] = {
            subject: standard.subject,
            grade: standard.grade,
            standardCode: standard.standardCode,
            title: standard.title,
            assessments: new Set(),
            studentsAssessed: 0,
            distribution: {
              exceeds: 0,
              meets: 0,
              approaches: 0,
              does_not_meet: 0,
            },
          };
        }

        aggregated[key].studentsAssessed += standard.studentsAssessed || 0;
        aggregated[key].distribution.exceeds += standard.distribution?.exceeds || 0;
        aggregated[key].distribution.meets += standard.distribution?.meets || 0;
        aggregated[key].distribution.approaches += standard.distribution?.approaches || 0;
        aggregated[key].distribution.does_not_meet += standard.distribution?.does_not_meet || 0;

        // Track unique assessments (though we can't accurately aggregate this)
        if (standard.assessments) {
          aggregated[key].assessments.add(standard.assessments);
        }
      });

      return Object.values(aggregated).map(std => ({
        ...std,
        assessments: std.assessments.size > 0 ? Math.max(...std.assessments) : 0,
      }));
    }

    // Filter by selected school
    return learning.standards.filter(standard => standard.schoolId === selectedSchoolId);
  }, [learning, selectedSchoolId]);

  const renderSystemCoherence = () => {
    // Calculate metrics for each feature bucket
    const totalSchools = usage?.schools?.length || 0;

    // Calculate quiz participation metrics
    let totalQuizInteractions = 0;
    let totalAssessmentInteractions = 0;
    let schoolsUsingQuizzes = 0;
    let schoolsUsingAssessments = 0;
    let assessmentParticipationPctSum = 0;
    let schoolsAndGradesCount = 0;
    usage?.schools?.forEach(school => {
      school.grades?.forEach(grade => {
        const quizPct = grade.quizParticipation?.quizParticipationPct || 0;
        const assessmentPct = grade.assessmentParticipation?.assessmentParticipationPct || 0;
        if (quizPct > 0) schoolsUsingQuizzes++;
        if (assessmentPct > 0) schoolsUsingAssessments++;
        const studentCount = grade.studentCount || 0;
        totalQuizInteractions += Math.round(studentCount * (quizPct / 100));
        totalAssessmentInteractions += Math.round(studentCount * (assessmentPct / 100));
        assessmentParticipationPctSum += grade.assessmentParticipation?.assessmentParticipationPct || 0;
        schoolsAndGradesCount++;
      });
    });
    schoolsUsingQuizzes = Math.min(schoolsUsingQuizzes, totalSchools); // Cap at total schools
    schoolsUsingAssessments = Math.min(schoolsUsingAssessments, totalSchools);
    if (schoolsAndGradesCount === 0) schoolsAndGradesCount = 1; // Prevent division by zero
    let assessmentParticipationPct = assessmentParticipationPctSum / schoolsAndGradesCount;

    // Assessment metrics come from learning data
    const assessmentInteractions = districtSummary.totalInteractions;

    // Student voice metrics
    let studentVoiceResponses = 0;
    let schoolsWithStudentVoice = 0;
    usage?.schools?.forEach(school => {
      school.grades?.forEach(grade => {
        const voiceRate = grade.studentVoice?.responseRatePct || 0;
        if (voiceRate > 0) schoolsWithStudentVoice++;
        const studentCount = grade.studentCount || 0;
        studentVoiceResponses += Math.round(studentCount * (voiceRate / 100));
      });
    });
    schoolsWithStudentVoice = Math.min(schoolsWithStudentVoice, totalSchools);

    const features = [
      {
        id: 1,
        title: 'Pacing Guides',
        description: 'Standards first pacing guides and resource implementation',
        enabled: true,
        metrics: [
          /*{ label: 'Content Areas', value: uniqueSubjects.length },
          { label: 'Teachers with Access', value: districtSummary.totalTeachers.toLocaleString() },*/
          { label: 'Quiz Completions', value: totalQuizInteractions.toLocaleString() },
          { label: 'Avg. Participation Rate', value: schoolsUsingQuizzes > 0 ? Math.round((totalQuizInteractions / districtSummary.totalStudents) * 100) + '%' : '0%' },

        ],
      },
      {
        id: 2,
        title: 'Assessments',
        description: 'Standards first common assessment implementation',
        enabled: true,
        metrics: [
          { label: 'Percent of common assessments given', value: `${Math.round(assessmentParticipationPct)}%` },
          { label: 'Avg. Student Participation Rate', value: schoolsUsingAssessments > 0 ? Math.round((totalAssessmentInteractions / districtSummary.totalStudents) * 100) + '%' : '0%' },
        ],
      },
      {
        id: 3,
        title: 'Teamwork',
        description: 'Data team protocols and plans',
        enabled: true,
        metrics: [
          { label: 'Implementation of data team protocols', value: `74%` },
          { label: 'Plans completed', value: `42%` },
        ],
      },
      /*
      {
        id: 4,
        title: 'Assessments',
        description: 'Comprehensive standards-based assessments measuring student mastery and growth',
        enabled: true,
        metrics: [
          { label: 'Standards Assessed', value: assessmentInteractions.toLocaleString() },
          { label: 'District-Wide Coverage', value: totalSchools + ' Schools' },
        ],
      },
      {
        id: 5,
        title: 'PLCs',
        description: 'Collaborative data team protocols for analyzing student work and planning interventions',
        enabled: false,
        metrics: [
          { label: 'PLC Cycles Completed', value: 0 },
          { label: 'Teacher Engagement', value: '0%' },
        ],
      },
      */
      {
        id: 6,
        title: 'MTSS & IEP Tracking',
        description: 'Multi-tiered system of supports and IEP management',
        enabled: true,
        metrics: [
          { label: 'Students in Tiers', value: '6,872' },
          { label: 'Active IEP Goals', value: '2,637' },
        ],
      },
      /*
      {
        id: 7,
        title: 'Student Voice',
        description: 'Student survey tool to measure engagement and gather student perspectives',
        enabled: true,
        metrics: [
          { label: 'Survey Responses', value: studentVoiceResponses.toLocaleString() },
          { label: 'Response Rate', value: Math.round((studentVoiceResponses / districtSummary.totalStudents) * 100) + '%' },
        ],
      },*/
    ];

    return (
      <div>
        <div style={{ marginBottom: 32 }}>
          <Title level={2} style={{ color: '#1098f7', marginBottom: 8, fontSize: 28, fontWeight: 'bold' }}>
            Platform Overview
          </Title>
          <Text style={{ color: '#262626', fontSize: 15, lineHeight: 1.6, display: 'block', marginBottom: 24 }}>
            See what features your district has access to and how teachers and students are using the platform
            to improve instruction and learning outcomes.
          </Text>
        </div>

        <Card
          style={{
            marginBottom: 32,
            border: '1px solid #e8e8e8',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
          bodyStyle={{ padding: 32 }}
        >
          <Title level={4} style={{ marginBottom: 24, fontSize: 18, fontWeight: 600 }}>
            District Overview
          </Title>
          <Row gutter={[32, 16]}>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#434343',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 8,
                }}>
                  {/*Students Covered*/}
                  Course Coherence
                </div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#1098f7', lineHeight: 1 }}>
                  {/*districtSummary.totalStudents.toLocaleString()*/}
                  {pacingGuidesCount.toLocaleString()}
                </div>
                <div>
                  Number of standards first pacing guides
                </div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#434343',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 8,
                }}>
                  Assessment Coherence
                </div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#1098f7', lineHeight: 1 }}>
                  {schoolsUsingAssessments > 0 ? Math.round(totalAssessmentInteractions).toLocaleString() : '0'}
                </div>
                <div style={{ fontSize: 13, color: '#262626', marginTop: 4 }}>
                  Total number of coherent common assessments
                </div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#434343',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 8,
                }}>
                  Teachers With Access
                </div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#1098f7', lineHeight: 1 }}>
                  {districtSummary.totalTeachers.toLocaleString()}
                </div>
                <div style={{ fontSize: 13, color: '#262626', marginTop: 4 }}>
                  {districtSummary.participatingTeachers.toLocaleString()} actively using
                </div>
              </div>
            </Col>
            {/*<Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#434343',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 8,
                }}>
                  Total Platform Engagement
                </div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#1098f7', lineHeight: 1 }}>
                  {(assessmentInteractions + totalQuizInteractions + studentVoiceResponses).toLocaleString()}
                </div>
                <div style={{ fontSize: 13, color: '#262626', marginTop: 4 }}>
                  Student interactions tracked
                </div>
              </div>
            </Col>*/}
          </Row>
        </Card>

        <Title level={3} style={{ marginBottom: 24, fontSize: 20, fontWeight: 600 }}>
          Platform Features & Adoption
        </Title>

        <Row gutter={[20, 20]}>
          {features.map((feature) => (
            <Col xs={24} md={12} lg={12} key={feature.id}>
              <Card
                style={{
                  border: feature.enabled ? '1px solid #1098f7' : '1px solid #faad14',
                  boxShadow: feature.enabled ? '0 2px 8px rgba(16, 152, 247, 0.1)' : '0 2px 8px rgba(250, 173, 20, 0.1)',
                  height: '100%',
                }}
                bodyStyle={{ padding: 20 }}
              >
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                    {feature.title}
                  </Title>
                  <Tag color={feature.enabled ? 'blue' : 'orange'} style={{ marginLeft: 8 }}>
                    {feature.enabled ? 'Active' : 'Upgrade'}
                  </Tag>
                </div>
                <Text style={{ color: '#595959', fontSize: 13, display: 'block', marginBottom: 16 }}>
                  {feature.description}
                </Text>
                <div style={{ backgroundColor: feature.enabled ? '#f0f5ff' : '#fff7e6', padding: 12, borderRadius: 6 }}>
                  {feature.metrics.map((metric, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: idx < feature.metrics.length - 1 ? 8 : 0,
                      }}
                    >
                      <Text style={{ fontSize: 13, color: '#595959' }}>{metric.label}</Text>
                      <Text strong style={{ fontSize: 15, color: '#262626' }}>{metric.value}</Text>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderLevelsOfUse = () => {
    if (!usage || !Array.isArray(usage.schools) || usage.schools.length === 0) {
      return <Empty description="Usage data not available" />;
    }

    return (
      <div>
        <div style={{ marginBottom: 32 }}>
          <Title level={2} style={{ color: '#1098f7', marginBottom: 8, fontSize: 28, fontWeight: 'bold' }}>
            Assessment Participation
          </Title>
          <Text style={{ color: '#262626', fontSize: 15, lineHeight: 1.6, display: 'block', marginBottom: 24 }}>
            Track how teachers and students across your district are using quizzes and assessments.
            Identify schools with strong engagement and those that may benefit from additional support.
          </Text>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Text strong style={{ fontSize: 14, color: '#262626' }}>Time Period:</Text>
            <Select
              value={selectedQuarter}
              onChange={setSelectedQuarter}
              style={{ width: 200 }}
            >
              <Select.Option value="overall">Overall (Full Year)</Select.Option>
              <Select.Option value="Q1">Quarter 1</Select.Option>
              <Select.Option value="Q2">Quarter 2</Select.Option>
              <Select.Option value="Q3">Quarter 3</Select.Option>
              <Select.Option value="Q4">Quarter 4</Select.Option>
            </Select>
          </div>
        </div>

        <Collapse bordered>
          {[...usage.schools].sort(sortSchools).map((school) => {
            const summary = school.summary || {};
            const teachers = summary.teachers || {};
            const dataTeams = summary.dataTeams || {};
            const mtssTotals = summary.mtss || {};
            const mtssCount = Object.values(mtssTotals).reduce(
              (sum, tier) => sum + ((tier && tier.total) || 0),
              0,
            );

            // Calculate school-wide metrics based on selected quarter
            const grades = school.grades || [];
            let quizParticipationSum = 0;
            let assessmentParticipationSum = 0;
            let gradeCount = 0;

            grades.forEach((grade) => {
              gradeCount++;

              // Get quiz participation for selected quarter
              const quizParticipation = selectedQuarter === 'overall'
                ? (grade.quizParticipation || {})
                : (grade.quizParticipationByQuarter || {})[selectedQuarter] || {};

              quizParticipationSum += quizParticipation.quizParticipationPct || 0;

              // Get assessment participation for selected quarter
              const assessmentParticipation = selectedQuarter === 'overall'
                ? (grade.assessmentParticipation || {})
                : (grade.assessmentParticipationByQuarter || {})[selectedQuarter] || {};

              assessmentParticipationSum += assessmentParticipation.assessmentParticipationPct || 0;
            });

            const schoolResourcePct = gradeCount > 0
              ? Number((quizParticipationSum / gradeCount).toFixed(1))
              : 0;

            const schoolAssessmentPct = gradeCount > 0
              ? Number((assessmentParticipationSum / gradeCount).toFixed(1))
              : 0;

            const header = (
              <div style={{ padding: '12px 0' }}>
                <div style={{ marginBottom: 16 }}>
                  <Title level={4} style={{ marginBottom: 4, fontSize: 20, color: '#000000' }}>
                    {school.schoolName || 'Unnamed School'}
                  </Title>
                  <Text style={{ fontSize: 14, color: '#434343' }}>
                    {summary.studentCount?.toLocaleString()} students
                  </Text>
                </div>

                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Card
                      size="small"
                      style={{
                        ...getCardStyleForMetric('assignments'),
                        border: `1px solid ${getCardStyleForMetric('assignments').borderColor}`,
                        borderLeft: `4px solid ${getCardStyleForMetric('assignments').borderColor}`,
                        height: '100%',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        borderRadius: 8,
                      }}
                      bodyStyle={{ padding: 20 }}
                    >
                      <div style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#434343',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 12,
                      }}>
                        Quiz Participation
                      </div>
                      <div
                        style={{
                          fontSize: 42,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: '#000000',
                        }}
                      >
                        {schoolResourcePct}%
                      </div>
                    </Card>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Card
                      size="small"
                      style={{
                        ...getCardStyleForMetric('assessments'),
                        border: `1px solid ${getCardStyleForMetric('assessments').borderColor}`,
                        borderLeft: `4px solid ${getCardStyleForMetric('assessments').borderColor}`,
                        height: '100%',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        borderRadius: 8,
                      }}
                      bodyStyle={{ padding: 20 }}
                    >
                      <div style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#434343',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 12,
                      }}>
                        Assessment Participation
                      </div>
                      <div
                        style={{
                          fontSize: 42,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: '#000000',
                        }}
                      >
                        {schoolAssessmentPct}%
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>
            );

            return (
              <Panel header={header} key={school.schoolId} style={{ marginBottom: 8 }}>
                <Table
                  columns={gradeColumns}
                  dataSource={buildGradeRows(school.grades || [], selectedQuarter)}
                  pagination={false}
                  locale={{ emptyText: 'No grade-level data yet' }}
                  size="middle"
                  bordered
                  rowClassName={(record, index) => index % 2 === 0 ? '' : 'table-row-light'}
                />
              </Panel>
            );
          })}
        </Collapse>
      </div>
    );
  };

  const renderStandardPerformanceBar = (distribution, studentsAssessed) => {
    const total = studentsAssessed || 1;
    const exceeds = Number(distribution.exceeds || 0);
    const meets = Number(distribution.meets || 0);
    const approaches = Number(distribution.approaches || 0);
    const doesNotMeet = Number(distribution.does_not_meet || 0);

    const exceedsPct = (exceeds / total) * 100;
    const meetsPct = (meets / total) * 100;
    const approachesPct = (approaches / total) * 100;
    const doesNotMeetPct = (doesNotMeet / total) * 100;

    return (
      <div>
        <div
          style={{
            display: 'flex',
            height: 32,
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          }}
        >
          {exceedsPct > 0 && (
            <Tooltip title={`Exceed: ${exceeds} (${exceedsPct.toFixed(1)}%)`}>
              <div
                style={{
                  width: `${exceedsPct}%`,
                  backgroundColor: '#237804',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {exceedsPct > 8 ? `${exceedsPct.toFixed(0)}%` : ''}
              </div>
            </Tooltip>
          )}
          {meetsPct > 0 && (
            <Tooltip title={`Meet: ${meets} (${meetsPct.toFixed(1)}%)`}>
              <div
                style={{
                  width: `${meetsPct}%`,
                  backgroundColor: '#52c41a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {meetsPct.toFixed(0)}%
              </div>
            </Tooltip>
          )}
          {approachesPct > 0 && (
            <Tooltip title={`Approach: ${approaches} (${approachesPct.toFixed(1)}%)`}>
              <div
                style={{
                  width: `${approachesPct}%`,
                  backgroundColor: '#faad14',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {approachesPct.toFixed(0)}%
              </div>
            </Tooltip>
          )}
          {doesNotMeetPct > 0 && (
            <Tooltip title={`Does Not Meet: ${doesNotMeet} (${doesNotMeetPct.toFixed(1)}%)`}>
              <div
                style={{
                  width: `${doesNotMeetPct}%`,
                  backgroundColor: '#ff4d4f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {doesNotMeetPct.toFixed(0)}%
              </div>
            </Tooltip>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: '#8c8c8c' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: '#237804',
                borderRadius: 2,
              }}
            />
            <span>Exceed: {exceeds}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: '#52c41a',
                borderRadius: 2,
              }}
            />
            <span>Meet: {meets}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: '#faad14',
                borderRadius: 2,
              }}
            />
            <span>Approach: {approaches}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: '#ff4d4f',
                borderRadius: 2,
              }}
            />
            <span>DNM: {doesNotMeet}</span>
          </div>
        </div>
      </div>
    );
  };

  const sortStandardCode = (codeA, codeB) => {
    // Split by dots to compare numerically where possible
    const partsA = codeA.split('.');
    const partsB = codeB.split('.');

    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      const partA = partsA[i] || '';
      const partB = partsB[i] || '';

      // Try to parse as numbers
      const numA = parseInt(partA, 10);
      const numB = parseInt(partB, 10);

      if (!isNaN(numA) && !isNaN(numB)) {
        if (numA !== numB) return numA - numB;
      } else {
        // String comparison
        if (partA !== partB) return partA.localeCompare(partB);
      }
    }
    return 0;
  };

  const renderSchoolComparison = () => {
    if (!learning) {
      return <Empty description="Student learning data not available" />;
    }

    return (
      <div>
        <div style={{ marginBottom: 32 }}>
          <Title level={2} style={{ color: '#1098f7', marginBottom: 8, fontSize: 28, fontWeight: 'bold' }}>
            School Comparison
          </Title>
          <Text style={{ color: '#262626', fontSize: 15, lineHeight: 1.6 }}>
            Compare student proficiency trends across schools over the academic year.
            Filter by content area to identify schools that need targeted support.
          </Text>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card
              title="School Performance Trends"
              bordered
              extra={
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: 13 }}>School Type:</Text>
                    <Select
                      value={selectedSchoolType}
                      onChange={setSelectedSchoolType}
                      style={{ width: 140 }}
                      size="small"
                    >
                      <Select.Option value="elementary">Elementary</Select.Option>
                      <Select.Option value="middle">Middle</Select.Option>
                      <Select.Option value="high">High School</Select.Option>
                    </Select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: 13 }}>Content Area:</Text>
                    <Select
                      value={selectedContentArea}
                      onChange={setSelectedContentArea}
                      style={{ width: 140 }}
                      size="small"
                    >
                      <Select.Option value="All Subjects">All Subjects</Select.Option>
                      <Select.Option value="Math">Math</Select.Option>
                      <Select.Option value="ELA">ELA</Select.Option>
                      <Select.Option value="Science">Science</Select.Option>
                      <Select.Option value="Social Studies">Social Studies</Select.Option>
                    </Select>
                  </div>
                </div>
              }
            >
              {(() => {
                // Use masteryTrendBySchool (overall) or masteryTrendBySchoolAndSubject (filtered)
                const masteryBySchool = selectedContentArea === 'All Subjects'
                  ? (learning.masteryTrendBySchool || {})
                  : (learning.masteryTrendBySchoolAndSubject || {});

                // Filter schools based on selected type
                const filteredSchoolData = [];

                Object.entries(masteryBySchool).forEach(([schoolId, trendDataOrSubjects]) => {
                  // If filtering by subject, extract the subject's trend data
                  let trendData;
                  if (selectedContentArea === 'All Subjects') {
                    trendData = trendDataOrSubjects;
                  } else {
                    // trendDataOrSubjects is an object like { "Math": [...], "ELA": [...] }
                    trendData = trendDataOrSubjects[selectedContentArea];
                  }
                  // Find school in usage data to get type
                  const school = usage?.schools?.find(s => s.schoolId === schoolId);
                  if (!school) return;

                  const schoolType = getSchoolType(school);

                  // Apply school type filter
                  if (schoolType !== selectedSchoolType) {
                    return;
                  }

                  // Only include schools with data
                  if (!trendData || trendData.length === 0) return;

                  filteredSchoolData.push({
                    schoolId,
                    schoolName: school.schoolName || schoolId,
                    trendData,
                    schoolType,
                  });
                });

                // Sort by Q4 performance (descending)
                filteredSchoolData.sort((a, b) => {
                  const aQ4 = a.trendData[a.trendData.length - 1]?.masteryPct || 0;
                  const bQ4 = b.trendData[b.trendData.length - 1]?.masteryPct || 0;
                  return bQ4 - aQ4;
                });

                if (filteredSchoolData.length === 0) {
                  return <Empty description="No school data available for selected filters" />;
                }

                // Generate distinct colors for schools
                const colors = [
                  '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
                  '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb',
                  '#fa541c', '#389e0d', '#096dd9', '#c41d7f', '#531dab',
                  '#0050b3', '#d4380d', '#ad6800', '#08979c', '#d48806',
                ];

                // Prepare data for line chart
                const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

                // Prepare table data
                const tableData = filteredSchoolData.map((school, idx) => {
                  const q1 = school.trendData[0]?.masteryPct || 0;
                  const q2 = school.trendData[1]?.masteryPct || 0;
                  const q3 = school.trendData[2]?.masteryPct || 0;
                  const q4 = school.trendData[3]?.masteryPct || 0;
                  const growth = q4 - q1;

                  return {
                    key: school.schoolId,
                    rank: idx + 1,
                    schoolName: school.schoolName,
                    q1,
                    q2,
                    q3,
                    q4,
                    growth,
                    trendData: [q1, q2, q3, q4],
                  };
                });

                // Find the maximum growth across all schools for normalization
                const maxGrowth = Math.max(...tableData.map(d => {
                  const range = Math.max(...d.trendData) - Math.min(...d.trendData);
                  return range;
                }));

                const tableColumns = [
                  {
                    title: 'Rank',
                    dataIndex: 'rank',
                    key: 'rank',
                    width: 90,
                    sorter: (a, b) => a.rank - b.rank,
                    render: (rank) => (
                      <Text strong style={{ fontSize: 14, color: '#595959' }}>
                        #{rank}
                      </Text>
                    ),
                  },
                  {
                    title: 'School Name',
                    dataIndex: 'schoolName',
                    key: 'schoolName',
                    width: 280,
                    sorter: (a, b) => a.schoolName.localeCompare(b.schoolName),
                    render: (name) => (
                      <Text strong style={{ fontSize: 14, color: '#262626' }}>{name}</Text>
                    ),
                  },
                  {
                    title: 'Q1',
                    dataIndex: 'q1',
                    key: 'q1',
                    width: 90,
                    align: 'center',
                    sorter: (a, b) => a.q1 - b.q1,
                    render: (val) => {
                      const backgroundColor =
                        val >= 80 ? '#f6ffed' :
                          val >= 70 ? '#fffbe6' :
                            val >= 50 ? '#fff7e6' : '#fff1f0';
                      return (
                        <div style={{ backgroundColor, padding: '8px 0', margin: '-8px 0' }}>
                          <Text style={{ fontSize: 13, color: '#595959' }}>{val.toFixed(1)}%</Text>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Q2',
                    dataIndex: 'q2',
                    key: 'q2',
                    width: 90,
                    align: 'center',
                    sorter: (a, b) => a.q2 - b.q2,
                    render: (val) => {
                      const backgroundColor =
                        val >= 80 ? '#f6ffed' :
                          val >= 70 ? '#fffbe6' :
                            val >= 50 ? '#fff7e6' : '#fff1f0';
                      return (
                        <div style={{ backgroundColor, padding: '8px 0', margin: '-8px 0' }}>
                          <Text style={{ fontSize: 13, color: '#595959' }}>{val.toFixed(1)}%</Text>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Q3',
                    dataIndex: 'q3',
                    key: 'q3',
                    width: 90,
                    align: 'center',
                    sorter: (a, b) => a.q3 - b.q3,
                    render: (val) => {
                      const backgroundColor =
                        val >= 80 ? '#f6ffed' :
                          val >= 70 ? '#fffbe6' :
                            val >= 50 ? '#fff7e6' : '#fff1f0';
                      return (
                        <div style={{ backgroundColor, padding: '8px 0', margin: '-8px 0' }}>
                          <Text style={{ fontSize: 13, color: '#595959' }}>{val.toFixed(1)}%</Text>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Q4',
                    dataIndex: 'q4',
                    key: 'q4',
                    width: 90,
                    align: 'center',
                    sorter: (a, b) => a.q4 - b.q4,
                    defaultSortOrder: 'descend',
                    render: (val) => {
                      const backgroundColor =
                        val >= 80 ? '#f6ffed' :
                          val >= 70 ? '#fffbe6' :
                            val >= 50 ? '#fff7e6' : '#fff1f0';
                      return (
                        <div style={{ backgroundColor, padding: '8px 0', margin: '-8px 0' }}>
                          <Text style={{ fontSize: 13, color: '#595959' }}>{val.toFixed(1)}%</Text>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Growth',
                    dataIndex: 'growth',
                    key: 'growth',
                    width: 100,
                    align: 'center',
                    sorter: (a, b) => a.growth - b.growth,
                    render: (growth) => {
                      const color = growth > 0 ? '#52c41a' : growth < 0 ? '#f5222d' : '#8c8c8c';
                      const arrow = growth > 0 ? '↑' : growth < 0 ? '↓' : '→';
                      return (
                        <Text strong style={{ fontSize: 13, color }}>
                          {arrow} {growth > 0 ? '+' : ''}{growth.toFixed(1)}%
                        </Text>
                      );
                    },
                  },
                  {
                    title: 'Trend',
                    dataIndex: 'trendData',
                    key: 'trend',
                    width: 100,
                    align: 'center',
                    render: (trendData) => {
                      // Normalize each sparkline by its own range, but scale relative to max growth
                      const localMin = Math.min(...trendData);
                      const localMax = Math.max(...trendData);
                      const localRange = localMax - localMin || 0.1; // Avoid division by zero

                      // Calculate how much vertical space to use based on growth relative to max
                      const heightScale = maxGrowth > 0 ? (localRange / maxGrowth) : 1;
                      const availableHeight = 40 * heightScale; // Scale based on relative growth
                      const centerY = 25; // Center of the 50px SVG

                      const points = trendData.map((val, idx) => {
                        const x = (idx / (trendData.length - 1)) * 80;
                        // Normalize to 0-1 range for this school
                        const normalized = (val - localMin) / localRange;
                        // Center around middle, scaled by relative growth
                        const y = centerY + (availableHeight / 2) - (normalized * availableHeight);
                        return `${x},${y}`;
                      }).join(' ');

                      return (
                        <svg width="80" height="50" style={{ display: 'block', margin: '0 auto' }}>
                          <polyline
                            points={points}
                            fill="none"
                            stroke="#1890ff"
                            strokeWidth="2"
                          />
                        </svg>
                      );
                    },
                  },
                ];

                return (
                  <div>
                    <div style={{ marginBottom: 16, fontSize: 13, color: '#595959' }}>
                      Showing {filteredSchoolData.length} school{filteredSchoolData.length !== 1 ? 's' : ''}
                    </div>

                    <Table
                      columns={tableColumns}
                      dataSource={tableData}
                      pagination={false}
                      size="middle"
                      bordered
                      rowClassName={(record) => {
                        // Color-code by Q4 performance
                        if (record.q4 >= 80) return 'row-high-performance';
                        if (record.q4 >= 70) return 'row-medium-performance';
                        return 'row-low-performance';
                      }}
                    />
                  </div>
                );
              })()}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const renderStudentLearning = () => {
    if (!learning) {
      return <Empty description="Student learning rollup not available" />;
    }

    const standardsRaw = filteredStandards;

    // Organize by grade → subject → standards
    const standardsByGrade = standardsRaw.reduce((acc, standard) => {
      const grade = standard.grade || 'Unknown';
      const subject = standard.subject || 'Unknown';

      if (!acc[grade]) {
        acc[grade] = {};
      }
      if (!acc[grade][subject]) {
        acc[grade][subject] = [];
      }
      acc[grade][subject].push(standard);
      return acc;
    }, {});

    // Sort standards within each subject
    Object.keys(standardsByGrade).forEach(grade => {
      Object.keys(standardsByGrade[grade]).forEach(subject => {
        standardsByGrade[grade][subject].sort((a, b) =>
          sortStandardCode(a.standardCode || '', b.standardCode || '')
        );
      });
    });

    const subjectOrder = ['ELA', 'Math', 'Science', 'Social Studies'];
    const gradeOrder = [
      'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
      '6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade',
      'Algebra', 'Geometry', 'Biology', 'Chemistry', 'Physics', 'United States History', 'World History'
    ];

    const sortedGrades = Object.keys(standardsByGrade).sort((a, b) => {
      const aIndex = gradeOrder.indexOf(a);
      const bIndex = gradeOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return (
      <div>
        <div style={{ marginBottom: 32 }}>
          <Title level={2} style={{ color: '#1098f7', marginBottom: 8, fontSize: 28, fontWeight: 'bold' }}>
            Standards Performance
          </Title>
          <Text style={{ color: '#262626', fontSize: 15, lineHeight: 1.6 }}>
            Detailed breakdown of student performance on individual standards across grades and subjects.
            Filter by school to see specific results.
          </Text>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card
              title="Individual Standards Performance by Grade Level"
              bordered
              extra={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ fontSize: 13, color: '#595959' }}>
                    Filter by School:
                  </Text>
                  <Select
                    value={selectedSchoolId}
                    onChange={setSelectedSchoolId}
                    style={{ width: 220 }}
                    size="small"
                  >
                    <Select.Option value="ALL">All Schools (District-Wide)</Select.Option>
                    {schools.map(school => (
                      <Select.Option key={school.id} value={school.id}>
                        {school.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              }
            >
              <Collapse bordered={false} defaultActiveKey={sortedGrades[0]}>
                {sortedGrades.map((grade) => {
                  const subjectsInGrade = standardsByGrade[grade];
                  const sortedSubjects = Object.keys(subjectsInGrade).sort((a, b) => {
                    const aIndex = subjectOrder.indexOf(a);
                    const bIndex = subjectOrder.indexOf(b);
                    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;
                    return aIndex - bIndex;
                  });

                  // Calculate grade-level summary
                  const allStandardsInGrade = Object.values(subjectsInGrade).flat();
                  const totalStudents = allStandardsInGrade.reduce((sum, s) => sum + (s.studentsAssessed || 0), 0);
                  const totalAssessments = new Set(allStandardsInGrade.map(s => s.standardCode)).size;

                  // Calculate total available standards for this grade across all subjects
                  const totalStandardsMap = learning.totalStandardsBySubjectGrade || {};
                  let totalAvailableStandards = 0;
                  Object.keys(subjectsInGrade).forEach(subject => {
                    const key = `${subject}|${grade}`;
                    const count = totalStandardsMap[key] || 0;
                    totalAvailableStandards += count;
                  });

                  // Aggregate distributions across all standards in this grade
                  const gradeDistribution = allStandardsInGrade.reduce(
                    (acc, standard) => {
                      const d = standard.distribution || {};
                      acc.exceeds += Number(d.exceeds || 0);
                      acc.meets += Number(d.meets || 0);
                      acc.approaches += Number(d.approaches || 0);
                      acc.does_not_meet += Number(d.does_not_meet || 0);
                      return acc;
                    },
                    { exceeds: 0, meets: 0, approaches: 0, does_not_meet: 0 }
                  );
                  const gradeTotalStudents = gradeDistribution.exceeds + gradeDistribution.meets +
                    gradeDistribution.approaches + gradeDistribution.does_not_meet;

                  return (
                    <Panel
                      header={
                        <div style={{ padding: '8px 0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <Text strong style={{ fontSize: 18, fontWeight: 600 }}>
                              {grade}
                            </Text>
                            <div style={{ display: 'flex', gap: 24, fontSize: 14 }}>
                              {totalAvailableStandards > 0 ? (
                                <Text type="secondary">
                                  {totalAssessments} of {totalAvailableStandards} standards assessed
                                </Text>
                              ) : (
                                <Text type="secondary">{totalAssessments} standards assessed</Text>
                              )}
                              <Text type="secondary">{totalStudents.toLocaleString()} student assessments</Text>
                            </div>
                          </div>
                          {gradeTotalStudents > 0 && (
                            <div style={{ marginTop: 8 }}>
                              {renderStandardPerformanceBar(gradeDistribution, gradeTotalStudents)}
                            </div>
                          )}
                        </div>
                      }
                      key={grade}
                      style={{ marginBottom: 8 }}
                    >
                      <Collapse bordered={false} ghost>
                        {sortedSubjects.map((subject) => {
                          const standards = subjectsInGrade[subject];
                          const subjectStudents = standards.reduce((sum, s) => sum + (s.studentsAssessed || 0), 0);

                          // Get total available standards for this subject + grade
                          const subjectGradeKey = `${subject}|${grade}`;
                          const totalAvailableForSubject = totalStandardsMap[subjectGradeKey] || 0;

                          // Aggregate distributions across all standards in this subject
                          const subjectDistribution = standards.reduce(
                            (acc, standard) => {
                              const d = standard.distribution || {};
                              acc.exceeds += Number(d.exceeds || 0);
                              acc.meets += Number(d.meets || 0);
                              acc.approaches += Number(d.approaches || 0);
                              acc.does_not_meet += Number(d.does_not_meet || 0);
                              return acc;
                            },
                            { exceeds: 0, meets: 0, approaches: 0, does_not_meet: 0 }
                          );
                          const subjectTotalStudents = subjectDistribution.exceeds + subjectDistribution.meets +
                            subjectDistribution.approaches + subjectDistribution.does_not_meet;

                          return (
                            <Panel
                              header={
                                <div style={{ padding: '4px 0' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <Text strong style={{ fontSize: 15, fontWeight: 500, color: '#262626' }}>
                                      {subject}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                      {totalAvailableForSubject > 0 ? (
                                        <>{standards.length} of {totalAvailableForSubject} standards assessed · </>
                                      ) : (
                                        <>{standards.length} standards assessed · </>
                                      )}
                                      {subjectStudents.toLocaleString()} student assessments
                                    </Text>
                                  </div>
                                  {subjectTotalStudents > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                      {renderStandardPerformanceBar(subjectDistribution, subjectTotalStudents)}
                                    </div>
                                  )}
                                </div>
                              }
                              key={`${grade}-${subject}`}
                              style={{ marginBottom: 4 }}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingLeft: 8 }}>
                                {standards.map((standard, idx) => {
                                  const distribution = standard.distribution || {};
                                  const total = standard.studentsAssessed || 0;
                                  const meets = Number(distribution.meets || 0);
                                  const exceeds = Number(distribution.exceeds || 0);
                                  const proficientPct = total > 0 ? ((meets + exceeds) / total) * 100 : 0;

                                  return (
                                    <div
                                      key={`${standard.subject}-${standard.grade}-${standard.standardCode || idx}`}
                                      style={{
                                        border: '1px solid #e8e8e8',
                                        borderRadius: 6,
                                        padding: 12,
                                        transition: 'all 0.2s',
                                        backgroundColor: '#fafafa',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                        e.currentTarget.style.backgroundColor = '#fff';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.backgroundColor = '#fafafa';
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                          marginBottom: 10,
                                          gap: 16,
                                        }}
                                      >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <Tag
                                              style={{
                                                fontFamily: 'monospace',
                                                fontSize: 12,
                                                fontWeight: 'bold',
                                                padding: '2px 8px',
                                                flexShrink: 0,
                                              }}
                                            >
                                              {standard.standardCode || standard.grade}
                                            </Tag>
                                            <Text style={{ fontSize: 13 }} type="secondary">
                                              {standard.assessments} assessment{standard.assessments !== 1 ? 's' : ''} · {total.toLocaleString()} students
                                            </Text>
                                          </div>
                                          {standard.title && (
                                            <Tooltip title={standard.title.length > 120 ? standard.title : null}>
                                              <div
                                                style={{
                                                  fontSize: 12,
                                                  color: '#8c8c8c',
                                                  lineHeight: 1.4,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                }}
                                              >
                                                {standard.title}
                                              </div>
                                            </Tooltip>
                                          )}
                                        </div>
                                        <Text
                                          style={{
                                            fontSize: 16,
                                            fontWeight: 'bold',
                                            color: proficientPct >= 70 ? '#52c41a' : proficientPct >= 50 ? '#faad14' : '#ff4d4f',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {proficientPct.toFixed(1)}% proficient
                                        </Text>
                                      </div>
                                      {renderStandardPerformanceBar(distribution, total)}
                                    </div>
                                  );
                                })}
                              </div>
                            </Panel>
                          );
                        })}
                      </Collapse>
                    </Panel>
                  );
                })}
              </Collapse>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Empty description={error} />;
  }

  if (!usage) {
    return <Empty description="District usage data not available" />;
  }

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="large"
        style={{
          backgroundColor: '#fff',
        }}
        tabBarStyle={{
          marginBottom: 0,
          paddingLeft: 24,
          paddingRight: 24,
          borderBottom: '2px solid #f0f0f0',
        }}
      >
        <TabPane
          tab={<span style={{ fontSize: 15, fontWeight: 500 }}>Platform Overview</span>}
          key="system"
        >
          <div style={{ padding: '32px 24px' }}>
            {renderSystemCoherence()}
          </div>
        </TabPane>
        <TabPane
          tab={<span style={{ fontSize: 15, fontWeight: 500 }}>School Comparison</span>}
          key="comparison"
        >
          <div style={{ padding: '32px 24px' }}>
            {renderSchoolComparison()}
          </div>
        </TabPane>
        <TabPane
          tab={<span style={{ fontSize: 15, fontWeight: 500 }}>Standards Performance</span>}
          key="learning"
        >
          <div style={{ padding: '32px 24px' }}>
            {renderStudentLearning()}
          </div>
        </TabPane>
        <TabPane
          tab={<span style={{ fontSize: 15, fontWeight: 500 }}>Assessment Participation</span>}
          key="usage"
        >
          <div style={{ padding: '32px 24px' }}>
            {renderLevelsOfUse()}
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default DistrictStatsV4;
